import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Users, CalendarCheck, PoundSterling, Handshake, Megaphone, Smartphone, ShieldCheck, Crown, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TopBar } from "@/components/Brand";
import { Overline } from "@/components/kit";
import { useAuth, homeFor } from "@/context/AuthContext";
import { api, initials, gbp } from "@/lib/api";
import { useSite, useSeo } from "@/lib/seo";
import { themeClass, themeStyle } from "@/lib/theme";
import { Sun, Moon } from "lucide-react";
import { IntervalToggle, PlanFeatures } from "@/pages/manage/Billing";

const ICONS = [Users, CalendarCheck, PoundSterling, Handshake, Megaphone, Smartphone];
const SPANS = ["md:col-span-2", "", "", "", "", "md:col-span-2"];

const FEATURES = [
  { icon: Users, title: "Squad & invites", text: "Add players by email — they get a link to finish registration. Add co-admins for coaches and treasurers.", span: "md:col-span-2" },
  { icon: CalendarCheck, title: "Fixtures & availability", text: "Matches, training and socials with one-tap RSVP and live headcounts.", span: "" },
  { icon: PoundSterling, title: "Fees in £", text: "Season subs or match fees. Players pay by card, you track paid vs owed.", span: "" },
  { icon: Handshake, title: "Sponsorship", text: "Tiered sponsor wall, deal values, renewals and an enquiry form on your hub.", span: "" },
  { icon: Megaphone, title: "Fans & news", text: "Fans follow by email, like posts, vote in Player of the Match polls.", span: "" },
  { icon: Smartphone, title: "Two installable apps", text: "A branded Fan app and a separate Player app — saved straight to the home screen.", span: "md:col-span-2" },
];

const ROLES = [
  { icon: Crown, title: "Super-admin", text: "Oversees every club on the platform. Pause or restore access instantly." },
  { icon: ShieldCheck, title: "Team admin", text: "Runs the club: squad, fixtures, fees, sponsors, fans, hub branding." },
  { icon: User, title: "Players", text: "Invited by email. Confirm availability, pay fees, read team news." },
];

const Hero = ({ site }) => {
  const { user } = useAuth();
  return (
    <section className="relative overflow-hidden grain">
      <img src={site.hero_image_url || "/img/stadium-lights.jpg"} alt="Hero" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to right, var(--hub-bg), color-mix(in srgb, var(--hub-bg) 90%, transparent), transparent)" }} />
      <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-20 sm:px-8 lg:pt-28">
        <Overline className="rise">{site.hero_overline}</Overline>
        <h1 className="rise mt-5 max-w-3xl font-display text-5xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-6xl lg:text-7xl" style={{ animationDelay: "80ms" }} data-testid="landing-hero-title">
          {site.hero_title} <span className="italic text-[#CCFF00]">{site.hero_title_accent}</span>
        </h1>
        <p className="rise mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg" style={{ animationDelay: "160ms" }}>
          {site.hero_subtitle}
        </p>
        <div className="rise mt-10 flex flex-wrap gap-3" style={{ animationDelay: "240ms" }}>
          {user ? (
            <Button asChild size="lg" className="rounded-full px-7 font-bold uppercase tracking-wider" data-testid="hero-dashboard-btn">
              <Link to={homeFor(user)}>Go to my dashboard</Link>
            </Button>
          ) : (
            <Button asChild size="lg" className="rounded-full px-7 font-bold uppercase tracking-wider" data-testid="hero-signup-btn">
              <Link to="/signup">{site.cta_primary}</Link>
            </Button>
          )}
          <Button asChild size="lg" variant="outline" className="rounded-full border-white/20 bg-transparent px-7 font-bold uppercase tracking-wider" data-testid="hero-demo-btn">
            <Link to="/t/riverside-rovers">{site.cta_secondary} <ArrowUpRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

const Features = ({ site }) => (
  <section className="pitch-lines border-y border-white/5">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
      <Overline>{site.features_overline}</Overline>
      <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">{site.features_title}</h2>
      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-4">
        {(site.features || []).map((f, i) => {
          const Icon = ICONS[i % ICONS.length];
          return (
          <div key={i} className={`rise group rounded-lg border border-white/[0.08] bg-[#111827] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#CCFF00]/40 ${SPANS[i % SPANS.length]}`} style={{ animationDelay: `${i * 60}ms` }} data-testid={`feature-card-${i}`}>
            <Icon className="h-6 w-6 text-[#CCFF00]" />
            <p className="mt-6 font-display text-2xl font-bold uppercase text-white">{f.title}</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.text}</p>
          </div>
          );
        })}
      </div>
    </div>
  </section>
);

const Roles = ({ site }) => (
  <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
    <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
      <div>
        <Overline>{site.roles_overline}</Overline>
        <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">{site.roles_title}</h2>
        <p className="mt-4 text-slate-400">{site.roles_text}</p>
      </div>
      <div className="space-y-4">
        {(site.roles || []).map((r, i) => {
          const Icon = [Crown, ShieldCheck, User][i % 3];
          return (
          <div key={i} className="flex gap-5 rounded-lg border border-white/[0.08] bg-[#111827] p-6" data-testid={`role-card-${i}`}>
            <Icon className="mt-1 h-6 w-6 shrink-0 text-[#CCFF00]" />
            <div>
              <p className="font-display text-xl font-bold uppercase text-white">{r.title}</p>
              <p className="mt-1 text-sm text-slate-400">{r.text}</p>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  </section>
);

const Pricing = ({ site }) => {
  const { user } = useAuth();
  const adminTeam = (user?.memberships || []).find((m) => m.role === "admin");
  const planLink = (p, interval, price) => (adminTeam ? `/manage/${adminTeam.team_id}?tab=billing` : price > 0 ? `/signup?plan=${p.id}&interval=${interval}` : "/signup");
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("month");
  useEffect(() => {
    api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);
  if (!plans.length) return null;
  return (
    <section id="pricing" className="border-y border-white/5 pitch-lines" data-testid="landing-pricing">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><Overline>{site.pricing_overline}</Overline><h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">{site.pricing_title}</h2><p className="mt-2 text-slate-400">{site.pricing_text}</p></div>
          <IntervalToggle value={interval} onChange={setInterval} />
        </div>
        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {plans.map((p) => {
            const price = interval === "month" ? p.price_monthly : p.price_yearly;
            return (
              <div key={p.id} className={`flex flex-col rounded-lg border bg-[#111827] p-7 ${p.highlight ? "border-[#CCFF00]/60" : "border-white/[0.08]"}`} data-testid={`pricing-plan-${p.id}`}>
                <p className="font-display text-2xl font-black uppercase text-white">{p.name}</p>
                <p className="mt-1 text-sm text-slate-400">{p.description}</p>
                <p className="mt-5 font-display text-5xl font-black text-white">{price > 0 ? gbp(price) : "Free"}<span className="ml-1 text-sm font-semibold text-slate-500">{price > 0 && (interval === "month" ? "/mo" : "/yr")}</span></p>
                <div className="mt-6 flex-1"><PlanFeatures features={p.features} /></div>
                <Button asChild className={`mt-6 rounded-full font-bold uppercase ${p.highlight ? "" : "border border-white/15 bg-transparent text-white hover:bg-white/5"}`} data-testid={`choose-plan-${p.name.toLowerCase()}`}>
                  <Link to={planLink(p, interval, price)}>{price > 0 ? `${site.pricing_cta || "Choose plan"}` : "Start free"}</Link>
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const SiteFooter = () => {
  const { site } = useSite();
  return (
    <footer className="border-t border-white/5 py-8" data-testid="site-footer">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 text-xs text-slate-500 sm:px-8">
        <span>{site?.footer_text}</span>
        <div className="flex flex-wrap gap-4">{(site?.footer_pages || []).map((p) => <Link key={p.slug} to={`/p/${p.slug}`} className="hover:text-white" data-testid={`footer-page-${p.slug}`}>{p.title}</Link>)}</div>
      </div>
    </footer>
  );
};

const Directory = ({ site }) => {
  const [teams, setTeams] = useState([]);
  useEffect(() => {
    api.get("/public/teams").then((r) => setTeams(r.data)).catch(() => {});
  }, []);
  if (!teams.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8">
      <Overline>{site.directory_title}</Overline>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {teams.map((t) => (
          <Link key={t.id} to={`/t/${t.slug}`} data-testid={`directory-team-${t.slug}`} className="group flex items-center gap-4 rounded-lg border border-white/[0.08] bg-[#111827] p-5 transition-colors hover:border-white/25">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md font-display text-lg font-black" style={{ background: t.primary_color, color: "#0B0F17" }}>{initials(t.name)}</span>
            <div className="min-w-0">
              <p className="truncate font-semibold text-white">{t.name}</p>
              <p className="text-xs text-slate-400">{t.sport}{t.location ? ` · ${t.location}` : ""}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default function Landing() {
  const { user } = useAuth();
  const { site } = useSite();
  const [mode, setMode] = useState(localStorage.getItem("sd-home-theme") || "dark");
  const toggle = () => { const m = mode === "dark" ? "light" : "dark"; localStorage.setItem("sd-home-theme", m); setMode(m); };
  useSeo();
  if (!site) return <div className="min-h-screen bg-[#0B0F17]" />;
  return (
    <div className={`min-h-screen bg-[#0B0F17] ${themeClass(mode)}`} style={themeStyle(mode, "#CCFF00")} data-testid="landing-page">
      {site.announcement && <div className="bg-[#CCFF00] px-4 py-2 text-center text-sm font-semibold text-[#0B0F17]" data-testid="site-announcement">{site.announcement}</div>}
      <TopBar>
        <button onClick={toggle} className="rounded-full border border-white/10 p-2 text-slate-300 hover:text-white" aria-label="Toggle light or dark mode" data-testid="home-theme-toggle">{mode === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</button>
        {user && <Button asChild size="sm" className="rounded-full font-bold" data-testid="nav-dashboard-btn"><Link to={homeFor(user)}>Dashboard</Link></Button>}
        {!user && (
          <>
            <Link to="/login" className="text-sm font-semibold text-slate-300 hover:text-white" data-testid="nav-login-link">Log in</Link>
            <Button asChild size="sm" className="rounded-full font-bold" data-testid="nav-signup-btn"><Link to="/signup">Sign up</Link></Button>
          </>
        )}
      </TopBar>
      <Hero site={site} />
      <Features site={site} />
      <Roles site={site} />
      <Pricing site={site} />
      <Directory site={site} />
      <SiteFooter />
    </div>
  );
}
